import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingProductManagementHelmet from 'common/helmets/LandingProductManagementHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import LandingCTAButtons from 'common/www/LandingCTAButtons';
import LandingFeaturesCTA from 'common/www/LandingFeaturesCTA';
import { Quote, QuotePerson, Quotes } from 'common/www/Quotes';
import Aurélien from 'img/landing/aurelien.webp';
import Point1 from 'img/landing/feature-request-tracking-1.webp';
import Point2 from 'img/landing/feature-request-tracking-3.webp';
import Jesse from 'img/landing/jesse.webp';
import LandingHero from 'img/landing/product-management-hero.webp';
import Point4 from 'img/landing/public-roadmap-1.webp';
import Point3_1x from 'img/landing/roadmap-1-1x.webp';
import Point3_2x from 'img/landing/roadmap-1-2x.webp';
import Tim from 'img/landing/tim-small.webp';

import 'css/components/www/_LandingSolutionsPage.scss';

class LandingProductManagement extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Product Management Page');
  }

  render() {
    return (
      <div className="landingProductManagement landingSolutionsPage">
        <LandingProductManagementHelmet />
        <div className="fullWidthContainer">
          <div className="header">
            <div className="left">
              <div className="text">
                <h1>Dedicated product management software helps you build better&nbsp;products</h1>
                <p className="subheading">
                  Empower your product team to make the most impactful decisions.
                </p>
                <LandingCTAButtons renderDemoButton />
              </div>
            </div>
            <LazyLoadedImage
              loading="eager"
              src={LandingHero}
              alt="A product manager looking at product management software"
            />
          </div>
        </div>
        <div className="mainContentContainer">
          <h2>
            Manage user feedback, prioritize feature development, create a product roadmap, and
            manage your&nbsp;changelog
          </h2>
          <div className="point">
            <LazyLoadedImage
              loading="eager"
              src={Point1}
              alt="Customer feedback organized in Canny's feedback management tool"
            />
            <div className="text">
              <h2 className="title">Organize all feedback in one product management&nbsp;tool</h2>
              <p>No more product feedback scattered across multiple&nbsp;channels.</p>
              <ul>
                <li>
                  Organized feedback means you can actually use it to inform product&nbsp;strategy
                </li>
                <li>Get clarification on feedback to better understand customer&nbsp;needs</li>
                <li>
                  Filter through the noise by using boards, categories, tags, and post&nbsp;owners
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage src={Point2} alt="Customer profiles and user behavior within Canny" />
            <div className="text">
              <h2 className="title">Understand exactly what your users&nbsp;want</h2>
              <p>
                Product planning works best when you understand customer needs and pain&nbsp;points.
              </p>
              <ul>
                <li>Know exactly which customer requested what and accurately judge&nbsp;impact</li>
                <li>
                  See what matters to specific customer groups using{' '}
                  <a href="https://canny.io/blog/user-segmentation/">user&nbsp;segmentation</a>
                </li>
                <li>
                  Use cases and additional clarity are neatly stored on a single Canny post,
                  allowing you to easily scope new features
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point3_1x}
              srcSet={`${Point3_1x} 1x, ${Point3_2x} 2x`}
              alt="Product management teams can prioritize features and build a roadmap"
            />
            <div className="text">
              <h2 className="title">Prioritize work and build a product&nbsp;roadmap</h2>
              <p>Make sure you’re working on the highest priority&nbsp;projects.</p>
              <ul>
                <li>
                  Score and rank feature requests and tasks based on impact, so you know you’re
                  working on the most important&nbsp;things
                </li>
                <li>Add projects to your roadmap to keep everyone on the same&nbsp;page</li>
                <li>
                  Integrate with your existing project management tools like Jira and&nbsp;GitHub
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point4}
              alt="A list of tasks under review, planned, and in progress in Canny"
            />
            <div className="text">
              <h2 className="title">Keep stakeholders in the&nbsp;loop</h2>
              <p>
                Product managers are responsible for communicating decisions to
                important&nbsp;stakeholders.
              </p>
              <ul>
                <li>Communicate progress with status updates and include&nbsp;ETAs</li>
                <li>
                  Publish entries to your <Link to="/features/product-changelog">changelog</Link> to
                  ensure people are aware of your new&nbsp;features
                </li>
                <li>Pull in specific stakeholders with internal&nbsp;comments</li>
              </ul>
            </div>
          </div>
          <LandingFeaturesCTA />
          <LandingFooterCTA style="solid" expanded={true} />
          <Quotes title={<>Product teams from industry-leading companies count on&nbsp;Canny</>}>
            <Quote
              header={<>GiveButter maintains a roadmap driven by customer&nbsp;feedback</>}
              text={
                <>
                  We want to let customer feedback really drive our roadmap. Not only on small
                  enhancements and changes, but also on our major features. Canny gives us a strong
                  sense of what customers want – both on the big picture features and “quality of
                  life”&nbsp;ones.
                </>
              }
              person={
                <QuotePerson
                  name="Jesse Sandala"
                  image={Jesse}
                  title={<>Director of Product,&nbsp;GiveButter</>}
                />
              }
            />
            <Quote
              header={<>Ahrefs developed a community-driven product development&nbsp;process</>}
              text={
                <>
                  If you're getting a lot of feedback and a big chunk of your time goes into
                  discussing requests. If you have a hard time figuring out which features you
                  should build next. If you want your product development to be community-driven.
                  Canny is an awesome tool. I'm sure you'll love&nbsp;it.
                </>
              }
              person={
                <QuotePerson
                  name="Tim Soulo"
                  image={Tim}
                  title={<>Chief Marketing Officer,&nbsp;Ahrefs</>}
                />
              }
            />
            <Quote
              header={
                <>
                  Strapi's product team simplifies user research and uncovers valuable
                  product&nbsp;data
                </>
              }
              text={
                <>
                  Product managers now have more context. We shortened the discovery time. Sometimes
                  we don't even need to send surveys anymore because we have enough people to
                  interact with. We can comment and engage with them when they share their
                  use&nbsp;case.
                </>
              }
              person={
                <QuotePerson
                  name="Aurélien Georget"
                  image={Aurélien}
                  title={<>Chief Product Officer,&nbsp;Strapi</>}
                />
              }
            />
          </Quotes>
          <FAQs
            faqs={[
              {
                question: 'What is Canny?',
                answer:
                  '<div><p>Glad you asked! Canny is product management software. It has all the tools product managers need. They can manage user feedback, prioritize feature requests, plan their roadmap, and publish a changelog.</p><p>Feedback management is central to our tools. We make it easy to collect user and customer feedback and organize it on easy-to-use feedback boards.</p><p>Canny is also a collaboration tool. Users can:</p><ul><li>Add new feature requests to your feedback boards</li><li>Comment, vote, and interact with other users and your team.</li></ul><p>This helps you understand your customer needs and user behavior. And you can build a better product!</p></div>',
                html: (
                  <>
                    <p>
                      Glad you asked! Canny is product management software. It has all the tools
                      product managers need. They can manage user feedback, prioritize feature
                      requests, plan their roadmap, and publish a changelog.
                    </p>
                    <p>
                      Feedback management is central to our tools. We make it easy to collect user
                      and customer feedback and organize it on easy-to-use feedback boards.
                    </p>
                    <p>Canny is also a collaboration tool. Users can:</p>
                    <ul className="orderedList">
                      <li className="orderedListItem">
                        Add new feature requests to your feedback boards
                      </li>
                      <li className="orderedListItem">
                        Comment, vote, and interact with other users and your team.
                      </li>
                    </ul>
                    <p>
                      This helps you understand your customer needs and user behavior. And you can
                      build a better product!
                    </p>
                  </>
                ),
              },
              {
                question: 'Does Canny integrate with other product management software and tools?',
                answer:
                  '<div><p>Yes! We integrate with many popular product management tools, project management software, and communication tools. That includes Jira Software, GitHub, ClickUp, Linear, Azure DevOps, Asana, Intercom, Zendesk, Salesforce, Hubspot, Slack, Microsoft Teams, Zapier, Segment, Make, Pebbly, Integrately, and Google Analytics.</p><p>Check out our <a target="_blank" href="https://canny.io/integrations">integrations</a> page for more details on these.</p></div>',
                html: (
                  <>
                    <p>
                      Yes! We integrate with many popular product management tools, project
                      management software, and communication tools. That includes Jira Software,
                      GitHub, ClickUp, Linear, Azure DevOps, Asana, Intercom, Zendesk, Salesforce,
                      Hubspot, Slack, Microsoft Teams, Zapier, Segment, Make, Pebbly, Integrately,
                      and Google Analytics.
                    </p>
                    <p>
                      Check out our{' '}
                      <a target="_blank" href="https://canny.io/integrations" rel="noopener">
                        integrations
                      </a>{' '}
                      page for more details on these.
                    </p>
                  </>
                ),
              },
              {
                question: 'Is Canny a project management tool?',
                answer:
                  "<p>Not exactly. While we do offer many tools that project managers find valuable, we're not a dedicated project management tool like Jira, GitHub, ClickUp, Asana, etc. We integrate with project management tools though! This helps you ‌handle planning and task management easily. That said, our product management tools can make a project manager's life easier.</p>",
                html: (
                  <p>
                    Not exactly. While we do offer many tools that project managers find valuable,
                    we're not a dedicated project management tool like Jira, GitHub, ClickUp, Asana,
                    etc. We integrate with project management tools though! This helps you ‌handle
                    planning and task management easily. That said, our product management tools can
                    make a project manager's life easier.
                  </p>
                ),
              },
              {
                question: 'How much does it cost?',
                answer:
                  "<div><p>We have plans to fit every company's needs! Our Free plan gives you all the essentials: product feedback management, a product roadmap, changelogs, and more.</p><p>Our Growth and Business plan provide additional tools and integrations for the product team that needs more functionality.</p></div>",
                html: (
                  <>
                    <p>
                      We have plans to fit every company's needs! Our Free plan gives you all the
                      essentials: product feedback management, a product roadmap, changelogs, and
                      more.
                    </p>
                    <p>
                      Our Growth and Business plan provide additional tools and integrations for the
                      product team that needs more functionality.
                    </p>
                  </>
                ),
              },
              {
                question: 'Can my whole team work in Canny?',
                answer:
                  "<div><p>Yes! All our plans include unlimited contributors. You can invite whoever you want to join your Canny account.</p><p>Many clients have admins and contributors from their product team, development team, design team, and customer success team.</p><p>Collaboration is easy! You can easily comment and tag each other on any feature requests you're working on.</p><p>Plus, with our Slack and Microsoft Teams integrations, you get notified as soon as a team member mentions you.</p></div>",
                html: (
                  <>
                    <p>
                      Yes! All our plans include unlimited contributors. You can invite whoever you
                      want to join your Canny account.
                    </p>
                    <p>
                      Many clients have admins and contributors from their product team, development
                      team, design team, and customer success team.
                    </p>
                    <p>
                      Collaboration is easy! You can easily comment and tag each other on any
                      feature requests you're working on.
                    </p>
                    <p>
                      Plus, with our Slack and Microsoft Teams integrations, you get notified as
                      soon as a team member mentions you.
                    </p>
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  }
}

export default compose(
  withContexts({ trackEvent: TrackEventContext }),
  hot,
)(LandingProductManagement);
